import { useEffect, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import {
  AggregateField,
  AggregateTarget,
} from '../../../../../../../../../domain/entity/WbsItemEntity'
import {
  calcProgressLineXPercent,
  calcTodayXPercent,
  ProgressLineInput,
} from '../CellRenderer/progressLine'
import { GanttDisplayUnit } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { useDatePositions } from '../CellRenderer/GanttBackground'
import { isGanttSummaryType } from '../ganttUtil'
import { Progress } from '../../../../../../../../pages/ProjectPlanNew/gridOptions/cumulation'
import objects from '../../../../../../../../../utils/objects'
import DateVO from '../../../../../../../../../vo/DateVO'

type ProgressLineResult = {
  points: { x: number; y: number }[]
  firstRowTop: number
  lastRowBottom: number
}

const EMPTY_RESULT: ProgressLineResult = {
  points: [],
  firstRowTop: 0,
  lastRowBottom: 0,
}

const calcPoints = (
  gridApi: GridApi,
  timeScale: CalendarDateVO[],
  ganttColumnWidth: number,
  aggregateTarget: AggregateTarget,
  aggregateField: AggregateField,
  dataPath: string,
  datePositions: { x: number; width: number; date: CalendarDateVO }[]
): ProgressLineResult => {
  const today = new DateVO(new Date())
  const now = today.toDate()
  const endOfToday = CalendarDateVO.of(today.addDays(1).toDate())
  const todayXPercent = calcTodayXPercent(datePositions, endOfToday)
  if (todayXPercent == null) return EMPTY_RESULT

  const prefix = dataPath ? `${dataPath}.` : ''
  const points: { x: number; y: number }[] = []
  let firstRowTop = 0
  let lastRowBottom = 0

  gridApi.forEachNodeAfterFilterAndSort(node => {
    const {
      displayed,
      data,
      allLeafChildren,
      rowTop: rawRowTop,
      rowHeight: rawRowHeight,
    } = node
    if (!displayed) return

    const body = objects.getValue(data, 'body')
    if (!body) return

    const wbsItem = objects.getValue(data, `${prefix}wbsItem`)
    if (!wbsItem) return

    const cumulation = objects.getValue(data, `${prefix}cumulation`)

    let progressRate = 0
    if (cumulation) {
      const progress = new Progress(cumulation, aggregateTarget, aggregateField)
      progressRate = progress.progressRate ?? 0
    }

    const hasChildren = (allLeafChildren && allLeafChildren.length > 0) || false

    const { status, wbsItemType, scheduledDate } = wbsItem
    const rawStart = scheduledDate?.startDate
    const rawEnd = scheduledDate?.endDate
    const input: ProgressLineInput = {
      status,
      wbsItemType,
      scheduledStartDate: rawStart ? new DateVO(rawStart).toDate() : undefined,
      scheduledEndDate: rawEnd ? new DateVO(rawEnd).toDate() : undefined,
      progressRate,
      hasChildren,
    }

    const rawXPercent = calcProgressLineXPercent(
      input,
      timeScale,
      now,
      todayXPercent
    )
    const x = (rawXPercent / 100) * ganttColumnWidth

    const rowTop = rawRowTop ?? 0
    const rowHeight = rawRowHeight ?? 32
    const setShift = isGanttSummaryType(wbsItemType) ? 0 : 3.5
    const y = rowTop + rowHeight / 2 - setShift

    if (points.length === 0) {
      firstRowTop = rowTop
    }
    lastRowBottom = rowTop + rowHeight

    points.push({ x, y })
  })

  return { points, firstRowTop, lastRowBottom }
}

export const useProgressLinePoints = (
  gridApi: GridApi | undefined,
  timeScale: CalendarDateVO[],
  ganttColumnWidth: number,
  aggregateTarget: AggregateTarget,
  aggregateField: AggregateField,
  dataPath: string,
  unit: GanttDisplayUnit
): ProgressLineResult => {
  const datePositions = useDatePositions(timeScale, unit)
  const [result, setResult] = useState<ProgressLineResult>(EMPTY_RESULT)

  useEffect(() => {
    if (!gridApi || timeScale.length === 0 || ganttColumnWidth <= 0) {
      setResult(EMPTY_RESULT)
      return
    }

    const update = () => {
      setResult(
        calcPoints(
          gridApi,
          timeScale,
          ganttColumnWidth,
          aggregateTarget,
          aggregateField,
          dataPath,
          datePositions
        )
      )
    }

    update()
    gridApi.addEventListener('modelUpdated', update)
    return () => gridApi.removeEventListener('modelUpdated', update)
  }, [
    datePositions,
    gridApi,
    timeScale,
    ganttColumnWidth,
    aggregateTarget,
    aggregateField,
    dataPath,
  ])

  return result
}
